import {
  Box,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  Hidden,
  TableBody,
  useTheme,
  makeStyles,
  Theme,
  useMediaQuery,
  ClickAwayListener,
  Tooltip,
  Link
} from "@material-ui/core";
import { colors } from "swyft-react-core";
import HelpIcon from "@material-ui/icons/Help";
import { useState } from "react";

import { QuoteDeduction } from "../../../../api/Quote/QuoteDeduction";
import { QuoteLineItem } from "../../../../api/Quote/QuoteLineItem";
import { Quote } from "../../../../api/Quote/Quote";
import {
  deductionSort,
  quoteDeductionValue,
  hasAddons,
  lineAddonSort,
  lineItemSort,
  quoteVat,
  solarGrantAmountAvailable,
  calculateVatPaid
} from "../../../../app/utils";
import { NewlineDisplay } from "../../../../components/NewlineDisplay";
import { FeatureAddons } from "../../../../app/consts";
import { useConfig } from "../../../../components/config/ConfigContext";
import { fontFamily } from "../../../../app/theme";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/rootReducer";
import { EQuotePurchaserType } from "../../../../api/Types";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

interface Props {
  quote: Quote | null;
}

export const QuoteTable = ({ quote }: Props) => {
  const { isFeatureEnabled } = useConfig();

  const { solarReport } = useSelector((state: RootState) => state.quote);

  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const matches = useMediaQuery("(max-width:1278px)");
  const { isMoreThanSm, isMoreThanMd } = useBreakpoint();
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    if (!isMoreThanMd)
      setTimeout(() => {
        setOpen(false);
      }, 10000);
  };

  var minWidth = isMoreThanSm ? 100 : 70;
  var minWidthCost = isMoreThanSm ? 110 : 85;

  if (quote === null) {
    return null;
  }

  const vat = quoteVat(quote);
  const includeVat = quote.purchaserType === EQuotePurchaserType.Home;

  const itemsView = (items?: QuoteLineItem[]) => {
    if (!items) {
      return null;
    }
    return items
      .slice()
      .sort(lineItemSort)
      .map((li) => <CollapsibleRow key={li.productId} lineItem={li} includeVat={includeVat} />);
  };

  const addonsView = (addons?: QuoteLineItem[]) => {
    if (!isFeatureEnabled(FeatureAddons)) {
      return null;
    }

    if (!hasAddons(addons)) {
      return null;
    }

    if (
      addons!.filter((value) => {
        if (value.selected) {
          return true;
        }
        return false;
      }).length === 0
    ) {
      return null;
    }

    const addonRender = addons!
      .slice()
      .sort(lineAddonSort)
      .map((li) => {
        if (!li.selected) {
          return null;
        }
        return <CollapsibleRow key={li.productId} lineItem={li} includeVat={includeVat} />;
      });

    return (
      <>
        <TableRow>
          <TableCell className={classes.detailsCell} colSpan={5}>
            <Box fontWeight="fontWeightBold" padding={1} pl={isSmall ? 4 : 6}>
              Additional items included
            </Box>
          </TableCell>
        </TableRow>
        {addonRender}
      </>
    );
  };

  const deductionsView = (deductions?: QuoteDeduction[]) => {
    if (!deductions) {
      return null;
    }
    return deductions
      .slice()
      .sort(deductionSort)
      .map((d) => <DeductionRow key={d.id} deduction={d} includeVat={includeVat} />);
  };

  const grantAmountAvailable = solarGrantAmountAvailable(quote, solarReport);

  return (
    <>
      <Table padding="none" className={classes.detailsTable}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "12px" : 0}
                py={isSmall ? "4px" : 0}
              >
                Item
              </Box>
            </TableCell>
            <TableCell align="center" width={minWidth}>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "0.5rem" : 0}
                py={isSmall ? "4px" : 0}
              >
                Qty
              </Box>
            </TableCell>
            <Hidden smDown>
              <TableCell align="right" width={minWidth}>
                <Box
                  borderTop={isSmall ? "1px solid #efefef" : "none"}
                  borderBottom={isSmall ? "1px solid #efefef" : "none"}
                  pl={isSmall ? "0.5rem" : 0}
                  py={isSmall ? "4px" : 0}
                >
                  <Typography noWrap={true} variant="inherit">
                    Unit Cost
                  </Typography>
                </Box>
              </TableCell>
            </Hidden>
            <TableCell align="right" width={minWidthCost}>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "0.5rem" : 0}
                py={isSmall ? "4px" : 0}
              >
                Cost
              </Box>
            </TableCell>
            <TableCell width={isSmall ? "21px" : "21px"}>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsView(quote.items)}
          {addonsView(quote.addons)}
          {deductionsView(quote.deductions)}

          <TableRow className={classes.resetRow}>
            <TableCell
              colSpan={isMoreThanMd ? 4 : 3}
              className={`${classes.detailsCell} padding-0 ${!matches ? "subtotal" : ""}`}
              align={"left"}
            >
              <Box
                display={"flex"}
                className="cell-box"
                justifyContent={"space-between"}
                borderTop={"1px solid #efefef"}
                pt={isSmall ? "24px" : "14px"}
                pb={isSmall ? "8px" : "14px"}
              >
                <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"}>
                  Subtotal
                </Box>
                <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"} pr={"14px"}>
                  <PriceFormat
                    value={quote.price - vat}
                    decimalScale={quote.purchaserType === EQuotePurchaserType.Home ? 2 : 0}
                  />
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>

          <TableRow className={classes.resetRow}>
            <TableCell
              colSpan={isMoreThanMd ? 4 : 3}
              className={`${classes.detailsCell} padding-0 ${!matches ? "subtotal" : ""}`}
              align={"left"}
            >
              <Box
                display={"flex"}
                className="cell-box"
                justifyContent={"space-between"}
                borderBottom={"1px solid #efefef"}
                py={isSmall ? "0px" : "14px"}
                pb={isSmall ? "24px" : "14px"}
              >
                <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"}>
                  VAT
                </Box>
                <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"} pr={"14px"}>
                  <PriceFormat value={vat} decimalScale={quote.purchaserType === EQuotePurchaserType.Home ? 2 : 0} />
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          <TableRow className={classes.resetRow}>
            <TableCell colSpan={isMoreThanMd ? 4 : 3} className={`${classes.detailsCell}`} align={"left"}>
              <Box
                display={"flex"}
                className="cell-box"
                justifyContent={"space-between"}
                pt={isSmall ? "25px" : "10px"}
              >
                <Box fontWeight={500} fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"} color={"#4b5462"}>
                  Total payable to Swyft Energy {quote?.purchaserType !== EQuotePurchaserType.Home && <>(incl. VAT)</>}
                </Box>
                <Box fontWeight={500} fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"} color={"#4b5462"}>
                  <PriceFormat
                    value={quote.price}
                    decimalScale={quote.purchaserType === EQuotePurchaserType.Home ? 2 : 0}
                  />
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {solarReport && grantAmountAvailable ? (
            <TableRow className={classes.resetRow}>
              <TableCell colSpan={isMoreThanMd ? 4 : 3} className={`${classes.detailsCell}`} align={"left"}>
                <Box display={"flex"} className="cell-box" justifyContent={"space-between"}>
                  <Box
                    fontWeight={500}
                    display={"flex"}
                    alignItems={"center"}
                    fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"}
                    color={"#4b5462"}
                  >
                    Total grants available
                    <Box
                      ml="10px"
                      display={"flex"}
                      style={{ cursor: "pointer" }}
                      mt={!isSmall ? "-4px" : "-2px"}
                      alignItems={"center"}
                    >
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <Tooltip
                          classes={{ popper: classes.summaryTooltip }}
                          PopperProps={{
                            disablePortal: true
                          }}
                          onClose={handleTooltipClose}
                          open={open}
                          arrow
                          placement="top"
                          disableFocusListener
                          disableHoverListener={!isMoreThanMd}
                          disableTouchListener
                          title={
                            <Box>
                              <Box mb={"6px"}>
                                Our grants team will guide you step-by-step through the grant application.
                              </Box>
                              <Box mb={"6px"}>
                                For domestic customers this will be payable to you by the SEAI up to 16 weeks after the
                                installation.
                              </Box>

                              <Box>For farmers this will be payable to you by the Department of Agriculture.</Box>
                            </Box>
                          }
                        >
                          <HelpIcon
                            fontSize="inherit"
                            htmlColor="#d8d8d8"
                            onClick={handleTooltipOpen}
                            onMouseEnter={handleTooltipOpen}
                          />
                        </Tooltip>
                      </ClickAwayListener>
                    </Box>
                  </Box>
                  <Box fontWeight={500} fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"} color={"#f7815c"}>
                    <PriceFormat value={grantAmountAvailable} prefix={"-€"} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          ) : null}
          <TableRow className={classes.resetRow}>
            <TableCell colSpan={isMoreThanMd ? 4 : 3} className={`${classes.detailsCell}`} align={"left"}>
              <Box display={"flex"} className="cell-box" justifyContent={"space-between"}>
                <Box fontWeight={700} fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"} color={"#43335b"}>
                  <span className={classes.borderBottom}>
                    Final cost to you {quote?.purchaserType !== EQuotePurchaserType.Home && <>(excl. VAT)</>}
                  </span>
                </Box>
                <Box fontWeight={700} fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"} color={"#43335b"}>
                  <span className={classes.borderBottom}>
                    <PriceFormat
                      value={
                        quote.price -
                        grantAmountAvailable -
                        (quote?.purchaserType === EQuotePurchaserType.Home ? 0 : quoteVat(quote))
                      }
                      decimalScale={quote.purchaserType === EQuotePurchaserType.Home ? 2 : 0}
                    />
                  </span>
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          <TableRow className={classes.resetRow}>
            <TableCell colSpan={isMoreThanMd ? 4 : 3} className={`${classes.detailsCell}`} align={"left"}>
              <Box color={"#888888"}>
                View Terms and Conditions{" "}
                <Link
                  href="https://www.swyftenergy.ie/terms-and-conditions-navigation"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

interface CollapsibleRowProps {
  lineItem: QuoteLineItem;
  includeVat: boolean;
}

const CollapsibleRow = ({ lineItem, includeVat }: CollapsibleRowProps) => {
  const classes = useStyles();
  const { isMoreThanSm } = useBreakpoint();

  let btnDelete = null;
  let quantity = <Box fontWeight={500}>{lineItem.quantity}</Box>;
  let name = lineItem.description;

  let price = includeVat ? lineItem.price : lineItem.price - calculateVatPaid(lineItem.price, lineItem.vatRate || 0);

  return (
    <>
      <TableRow>
        <TableCell className={classes.detailsCell}>
          <Box display="flex" alignItems="center">
            <NewlineDisplay text={name} />
          </Box>
        </TableCell>
        <TableCell className={classes.detailsCell} align="center">
          <Box fontSize={isMoreThanSm ? "14px" : "12px"}>{quantity}</Box>
        </TableCell>
        <Hidden smDown>
          <TableCell className={classes.detailsCell} align="right">
            <Box fontWeight={500}>
              <PriceFormat value={price} />
            </Box>
          </TableCell>
        </Hidden>
        <TableCell className={classes.detailsCell} align="right">
          <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"}>
            <PriceFormat value={lineItem.quantity * price} />
          </Box>
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          {btnDelete}
        </TableCell>
      </TableRow>
    </>
  );
};

const DeductionRow = (props: { deduction: QuoteDeduction; includeVat: boolean }) => {
  const classes = useStyles();
  const { isMoreThanSm } = useBreakpoint();
  const { deduction, includeVat } = props;

  return (
    <>
      <TableRow>
        <TableCell className={classes.detailsCell} style={{ whiteSpace: "pre-wrap" }}>
          {deduction.description}
        </TableCell>
        <TableCell className={classes.detailsCell} align="center">
          <Box fontSize={isMoreThanSm ? "14px" : "12px"}>1</Box>
        </TableCell>
        <Hidden smDown>
          <TableCell className={classes.detailsCell} align="right">
            &nbsp;
          </TableCell>
        </Hidden>
        <TableCell className={classes.detailsCell} align="right">
          <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"} color={"#f7815c"}>
            <PriceFormat value={quoteDeductionValue(deduction, includeVat)} />
          </Box>
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          &nbsp;
        </TableCell>
      </TableRow>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  detailsTable: {
    "fontFamily": fontFamily.Axiforma,
    "& th:not(:first-child)": {
      paddingLeft: "0.5rem",
      [theme.breakpoints.down("md")]: {
        paddingLeft: "0rem",
        paddingRight: 0
      }
    },
    "& th": {
      fontWeight: "bold",
      border: 0,
      padding: "0.5rem",
      fontSize: "12px",
      textTransform: "uppercase",
      paddingLeft: theme.spacing(6),
      color: "#4b5462",
      fontFamily: fontFamily.Axiforma,
      backgroundColor: colors.white,
      [theme.breakpoints.down("md")]: {
        paddingLeft: "21px",
        paddingRight: 0,
        paddingBottom: "15px"
      }
    },
    "& td": {
      padding: "14px",
      fontSize: "14px",
      lineHeight: "1.79",
      fontFamily: fontFamily.Axiforma,
      paddingLeft: theme.spacing(6),
      color: "#4b5462",
      borderBottom: "none",
      [theme.breakpoints.down("md")]: {
        padding: "0.5rem",
        paddingLeft: "33px"
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: "10px"
      }
    },
    "& td:not(:first-child)": {
      paddingLeft: "0.5rem"
    },
    "& tr:nth-of-type(odd)": {
      backgroundColor: colors.alternate
    }
  },
  detailsCell: {
    "fontFamily": fontFamily.Axiforma,
    "borderBottom": "0 !important",
    "padding": "14px 0",
    "verticalAlign": "middle",
    "&:last-child": {
      padding: "14px 0"
    },
    [theme.breakpoints.down("md")]: {
      "padding": "0.5rem 0",
      "&:last-child": {
        padding: "0.5rem 0"
      }
    }
  },
  borderBottom: {
    borderBottom: "2px solid"
  },
  resetRow: {
    "backgroundColor": "#FFFFFF !important",
    "padding": "14px 0",
    "& .p-0": {
      padding: 0,
      paddingLeft: theme.spacing(2),
      paddingTop: "5px"
    },
    "& .padding-0": {
      "padding": 0,
      "paddingLeft": theme.spacing(6),
      [theme.breakpoints.down("md")]: {
        paddingLeft: "33px",
        paddingTop: "0.5rem",
        paddingBottom: "0px"
      },
      "&:not(:first-child)": {
        paddingLeft: "0"
      }
    },
    "& .subtotal": {
      "paddingLeft": "17px",
      "& .cell-box": {
        paddingLeft: "31px"
      }
    }
  },
  button: {
    width: "90%",
    maxWidth: "17.5rem"
  },
  btnDelete: {
    minWidth: "0px"
  },
  doubleButton: {
    '& [class*="Button-root"]': {
      minWidth: "10rem",
      padding: "6px 8px",
      width: "auto"
    }
  },
  summaryTooltip: {
    "& .MuiTooltip-tooltip": {
      background: "#fff",
      color: "#5e6672",
      fontSize: "10px",
      fontFamily: fontFamily.Axiforma,
      padding: "9px 16px",
      boxShadow: "0 5px 15px 0 rgba(0, 0, 0, 0.07)"
    },
    "& .MuiTooltip-arrow": {
      color: "#fff"
    }
  }
}));
